import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { ServiceLeadForm } from '@/components/ServiceLeadForm';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, ArrowRight, Check, Star, Globe, Search, Megaphone, Phone, Target, BarChart3, Zap, Users } from 'lucide-react';

// Images - Marketing digital moderne
const HERO_IMAGE = "https://images.pexels.com/photos/6476185/pexels-photo-6476185.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CONTENT_IMAGE_1 = "https://images.pexels.com/photos/6476253/pexels-photo-6476253.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CONTENT_IMAGE_2 = "https://images.unsplash.com/photo-1758873271824-a3216c80d1ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxkaWdpdGFsJTIwbWFya2V0aW5nJTIwdGVhbSUyMGxhcHRvcCUyMG1vZGVybiUyMG9mZmljZXxlbnwwfHx8fDE3NzQxMjkwNzZ8MA&ixlib=rb-4.1.0&q=85";

export const MarketingPage = () => {
  const questions = [
    { id: 'besoin', label: 'Quel est votre besoin principal ?', type: 'select', options: ['Identité visuelle / Logo', 'Site web', 'SEO / Référencement', 'Publicité en ligne', 'Google My Business', 'Réseaux sociaux', 'Pack complet'] },
    { id: 'secteur', label: 'Secteur d\'activité ?', type: 'text', placeholder: 'Ex: Immobilier, hôtellerie, commerce...' },
    { id: 'localisation', label: 'Zone d\'activité ?', type: 'text', placeholder: 'Ex: Lausanne et environs' },
    { id: 'existant', label: 'Avez-vous déjà un site ou une présence digitale ?', type: 'select', options: ['Oui, mais à améliorer', 'Oui, mais obsolète', 'Non, à créer', 'Je ne sais pas'] },
    { id: 'budget', label: 'Budget approximatif ?', type: 'select', options: ['< 2\'000 CHF', '2\'000 - 5\'000 CHF', '5\'000 - 10\'000 CHF', '> 10\'000 CHF', 'À définir'] }
  ];

  const services = [
    { icon: '🎨', title: 'Logo & Identité', desc: 'Logo, charte graphique, supports print' },
    { icon: Globe, title: 'Création site web', desc: 'Site vitrine, e-commerce, landing pages' },
    { icon: Search, title: 'SEO & Référencement', desc: 'Visibilité Google, contenu optimisé' },
    { icon: Megaphone, title: 'Publicité Google/Meta', desc: 'Campagnes ciblées, ROI mesurable' },
    { icon: '📍', title: 'Google My Business', desc: 'Fiche optimisée, avis, visibilité locale' },
    { icon: '📱', title: 'Réseaux sociaux', desc: 'Stratégie, contenu, community management' }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="marketing-page">
      <Header />
      
      {/* ============================================
          PILIER 1: ATTENTION - Hero avec équipe marketing
          ============================================ */}
      <section className="hero-section relative min-h-[70vh] sm:min-h-[80vh] flex items-center">
        <img 
          src={HERO_IMAGE} 
          alt="Équipe marketing digital en réunion stratégique" 
          className="hero-image-bg"
        />
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8 py-10 sm:py-16 md:py-20">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
              <TrendingUp className="h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10 text-white" />
              <Badge className="bg-white/20 text-white border-white/30 backdrop-blur-sm text-xs sm:text-sm">
                Homely Marketing
              </Badge>
            </div>
            
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 text-white leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              Boostez votre visibilité et générez des leads qualifiés
            </h1>
            
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 mb-4 sm:mb-6 max-w-2xl">
              Identité visuelle, site web, SEO, Google My Business, publicité et réseaux sociaux : 
              nous développons votre présence digitale pour attirer plus de clients en Suisse romande.
            </p>

            {/* Métriques clés */}
            <div className="flex flex-wrap gap-2 sm:gap-3 md:gap-4 mb-5 sm:mb-8">
              <div className="flex items-center gap-1.5 sm:gap-2 bg-white/10 backdrop-blur-sm px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full">
                <Target className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
                <span className="text-white text-xs sm:text-sm font-semibold">+150% leads en moyenne</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 bg-white/10 backdrop-blur-sm px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full">
                <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" />
                <span className="text-white text-xs sm:text-sm">ROI mesurable</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 bg-white/10 backdrop-blur-sm px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full">
                <Zap className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" />
                <span className="text-white text-xs sm:text-sm">Stratégie sur mesure</span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button 
                size="lg" 
                className="btn-premium h-12 sm:h-14 px-6 sm:px-10 text-sm sm:text-base rounded-full w-full sm:w-auto"
                onClick={() => document.getElementById('form').scrollIntoView({ behavior: 'smooth' })}
                data-testid="marketing-cta-primary"
              >
                Audit gratuit de ma présence
                <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          PILIER 2: ÉMOTION - Le problème
          ============================================ */}
      <section className="py-12 sm:py-16 md:py-20">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12 items-center">
            <div>
              <Badge className="mb-3 sm:mb-4 bg-orange-100 text-orange-700 text-xs sm:text-sm">Le constat</Badge>
              
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Sans visibilité digitale, vos concurrents captent vos clients
              </h2>
              
              <p className="text-lg text-muted-foreground mb-6">
                90% des consommateurs cherchent sur Google avant d'acheter. Si vous n'êtes pas visible, 
                vous n'existez pas. Pendant ce temps, vos concurrents investissent et prennent votre place.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  { icon: '👻', title: 'Invisible sur Google', desc: 'Vos prospects ne vous trouvent pas, ils vont chez la concurrence' },
                  { icon: '🦕', title: 'Site obsolète', desc: 'Un site daté donne une image non professionnelle' },
                  { icon: '📉', title: 'Zéro leads', desc: 'Pas de formulaire, pas de tracking, pas de conversion' },
                  { icon: '💰', title: 'Budget gaspillé', desc: 'Publicité mal ciblée = argent jeté par les fenêtres' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4 p-4 bg-secondary/30 rounded-lg">
                    <span className="text-2xl">{item.icon}</span>
                    <div>
                      <p className="font-semibold">{item.title}</p>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-lg font-semibold" style={{ color: 'var(--sage-dark)' }}>
                Et si vous aviez une stratégie digitale qui génère vraiment des clients ?
              </p>
            </div>

            <div className="relative">
              <img 
                src={CONTENT_IMAGE_1} 
                alt="Équipe analysant des données marketing sur écran" 
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-lg max-w-xs">
                <p className="font-bold text-3xl" style={{ color: 'var(--sage)' }}>+150%</p>
                <p className="text-sm text-muted-foreground">de leads en moyenne pour nos clients</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          PILIER 3: MÉMOIRE - Nos services
          ============================================ */}
      <section className="py-20 gradient-premium">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4" style={{ backgroundColor: 'var(--sage)', color: 'white' }}>Nos services</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Une offre marketing complète pour votre croissance
            </h2>
            <div className="section-divider"></div>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              De l'identité visuelle à la publicité ciblée, nous couvrons tous les aspects 
              de votre présence digitale.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-16">
            {services.map((service, idx) => (
              <div key={idx} className="card-premium p-6 text-center">
                {typeof service.icon === 'string' ? (
                  <span className="text-4xl mb-4 block">{service.icon}</span>
                ) : (
                  <service.icon className="h-10 w-10 mx-auto mb-4" style={{ color: 'var(--sage)' }} />
                )}
                <h3 className="font-semibold mb-2">{service.title}</h3>
                <p className="text-sm text-muted-foreground">{service.desc}</p>
              </div>
            ))}
          </div>

          {/* Image + Liste avantages */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <img 
              src={CONTENT_IMAGE_2} 
              alt="Professionnelle du marketing digital en communication" 
              className="rounded-2xl shadow-xl w-full"
            />
            <div>
              <h3 className="text-2xl font-bold mb-6">Pourquoi nous confier votre marketing ?</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  'Stratégie sur mesure',
                  'Connaissance du marché suisse',
                  'Expertise SEO local',
                  'Design moderne et percutant',
                  'Rédaction persuasive',
                  'Publicité ROI-focused',
                  'Reporting transparent',
                  'Accompagnement personnalisé',
                  'Technologies récentes',
                  'Résultats mesurables',
                  'Réactivité',
                  'Budget optimisé'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          PILIER 4: DÉCISION - Preuves sociales
          ============================================ */}
      <section className="py-20">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          
          {/* Stats bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 p-8 rounded-2xl mb-16" style={{ backgroundColor: 'var(--sage)' }}>
            {[
              { value: '45+', label: 'Projets réalisés' },
              { value: '+150%', label: 'Leads en moyenne' },
              { value: '95%', label: 'Clients satisfaits' },
              { value: '#1', label: 'SEO local visé' }
            ].map((stat, idx) => (
              <div key={idx} className="text-center text-white">
                <p className="text-4xl font-bold mb-1">{stat.value}</p>
                <p className="text-sm text-white/80">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Témoignages */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Ils ont boosté leur visibilité avec nous
            </h2>
            <div className="section-divider"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              { 
                name: 'Cabinet dentaire Lux', 
                city: 'Lausanne', 
                service: 'Site web + SEO',
                text: 'Notre nouveau site attire 3x plus de patients qu\'avant. Le référencement local nous place en première page pour "dentiste Lausanne".' 
              },
              { 
                name: 'Restaurant Le Panorama', 
                city: 'Montreux', 
                service: 'Google My Business',
                text: 'Depuis l\'optimisation de notre fiche Google, nos réservations ont augmenté de 60%. Les avis positifs s\'accumulent !' 
              },
              { 
                name: 'Immobilier Léman SA', 
                city: 'Genève', 
                service: 'Pack complet',
                text: 'Nouveau logo, site performant, campagnes Google Ads : notre image a fait un bond en avant et nous générons 5x plus de leads vendeurs.' 
              }
            ].map((review, idx) => (
              <div key={idx} className="card-premium p-6">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-4 italic">"{review.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[var(--sage)] flex items-center justify-center text-white font-bold">
                    {review.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-sm">{review.name}</p>
                    <p className="text-xs text-muted-foreground">{review.service} • {review.city}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Avantages rapides */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Target, title: 'Stratégie ciblée', desc: 'Nous analysons votre marché et vos concurrents pour une stratégie gagnante' },
              { icon: BarChart3, title: 'Résultats mesurables', desc: 'Tableaux de bord et reporting mensuel pour suivre votre ROI' },
              { icon: Users, title: 'Accompagnement', desc: 'Un consultant dédié qui comprend vos enjeux et vos objectifs' }
            ].map((item, i) => (
              <div key={i} className="text-center p-8 rounded-2xl" style={{ backgroundColor: 'rgba(168, 179, 156, 0.1)' }}>
                <item.icon className="h-10 w-10 mx-auto mb-4" style={{ color: 'var(--sage)' }} />
                <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--sage-dark)' }}>{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          PILIER 5: ACTION - Formulaire
          ============================================ */}
      <section className="py-20" style={{ backgroundColor: 'var(--anthracite)' }} id="form">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="text-white">
              <Badge className="mb-4 bg-[var(--sage)]">Audit gratuit</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Analysons ensemble votre présence digitale
              </h2>
              <p className="text-white/70 mb-8 text-lg">
                Recevez un audit personnalisé de votre visibilité en ligne avec des recommandations 
                concrètes pour attirer plus de clients.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  'Analyse de votre site et de votre SEO',
                  'Audit de votre présence Google',
                  'Benchmark de vos concurrents',
                  'Recommandations prioritaires',
                  'Sans engagement'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-[var(--sage)] flex items-center justify-center">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-white/90">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <ServiceLeadForm service="clients" pole="marketing" questions={questions} />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section pour SEO */}
      <section className="py-16 border-t">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold mb-8 text-center">Questions fréquentes</h2>
          <div className="max-w-3xl mx-auto space-y-6">
            {[
              { q: 'Combien coûte un site web ?', a: 'Un site vitrine professionnel commence à 2\'500 CHF. Un site e-commerce ou avec fonctionnalités avancées : à partir de 5\'000 CHF. Nous établissons un devis sur mesure.' },
              { q: 'Combien de temps pour voir des résultats SEO ?', a: 'Le SEO est un travail de fond. Comptez 3-6 mois pour des résultats significatifs. En revanche, la publicité Google Ads génère des leads immédiatement.' },
              { q: 'Travaillez-vous avec des TPE/PME ?', a: 'Absolument ! Nous adaptons nos offres à votre budget et vos objectifs, que vous soyez indépendant ou PME.' },
              { q: 'Proposez-vous un suivi mensuel ?', a: 'Oui, nous proposons des formules d\'accompagnement mensuel incluant maintenance, reporting et optimisations continues.' }
            ].map((faq, idx) => (
              <div key={idx} className="p-6 bg-secondary/20 rounded-lg">
                <h3 className="font-semibold mb-2">{faq.q}</h3>
                <p className="text-muted-foreground">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
